import { useState, useRef } from 'react';
import PageHero from '../../components/feature/PageHero';

const NAV_LINKS = [
  { href: '/', icon: 'ri-home-line', label: 'Home' },
  { href: '/olympics-props', icon: 'ri-medal-line', label: 'Olympics' },
  { href: '/super-bowl-props', icon: 'ri-football-line', label: 'Super Bowl' },
  { href: '/markets', icon: 'ri-bar-chart-2-line', label: 'Markets' },
  { href: '/why-rtj', icon: 'ri-question-line', label: 'Why L2L' },
  { href: '/my-account', icon: 'ri-user-line', label: 'My Account' },
];

type AccountTab = 'overview' | 'active-bets' | 'bet-history' | 'settings';

const ACTIVE_BETS = [
  { id: 'AB001', market: 'Super Bowl LX Spread', selection: 'Seattle -4.5', stake: '$200.00', potentialPayout: '$382.00', potentialProfit: '+$182.00', status: 'live', prob: '52%', sport: 'NFL' },
  { id: 'AB002', market: 'Anytime TD Scorer', selection: 'K. Walker (SEA)', stake: '$150.00', potentialPayout: '$232.50', potentialProfit: '+$82.50', status: 'live', prob: '64%', sport: 'NFL' },
  { id: 'AB003', market: 'NBA MVP 2024-25', selection: 'Nikola Jokic (DEN)', stake: '$300.00', potentialPayout: '$576.92', potentialProfit: '+$276.92', status: 'pending', prob: '39%', sport: 'NBA' },
  { id: 'AB004', market: 'Champions League Winner', selection: 'Real Madrid', stake: '$250.00', potentialPayout: '$925.93', potentialProfit: '+$675.93', status: 'pending', prob: '27%', sport: 'Soccer' },
  { id: 'AB005', market: 'Most Receiving Yards SB', selection: 'J. Smith-Njigba (SEA)', stake: '$100.00', potentialPayout: '$158.73', potentialProfit: '+$58.73', status: 'live', prob: '63%', sport: 'NFL' },
];

const BET_HISTORY = [
  { id: 'BH001', market: 'Chiefs ML vs Raiders', selection: 'Kansas City Chiefs', stake: '$450.00', payout: '$633.80', profit: '+$183.80', result: 'won', date: '2 hours ago', sport: 'NFL' },
  { id: 'BH002', market: 'Eagles vs Cowboys Spread', selection: 'Eagles -3.5', stake: '$200.00', payout: '$0.00', profit: '-$200.00', result: 'lost', date: '5 hours ago', sport: 'NFL' },
  { id: 'BH003', market: '3-Leg Parlay', selection: 'Chiefs ML + Mahomes 2.5+ TDs + Over 48.5', stake: '$100.00', payout: '$950.00', profit: '+$850.00', result: 'won', date: '1 day ago', sport: 'NFL' },
  { id: 'BH004', market: 'Celtics vs Lakers ML', selection: 'Boston Celtics', stake: '$300.00', payout: '$483.87', profit: '+$183.87', result: 'won', date: '2 days ago', sport: 'NBA' },
  { id: 'BH005', market: 'Jokic Rebounds O/U 12.5', selection: 'Over 12.5 Rebounds', stake: '$150.00', payout: '$0.00', profit: '-$150.00', result: 'lost', date: '3 days ago', sport: 'NBA' },
  { id: 'BH006', market: 'Wimbledon Men\'s Singles', selection: 'Carlos Alcaraz', stake: '$200.00', payout: '$588.24', profit: '+$388.24', result: 'won', date: '4 days ago', sport: 'Tennis' },
  { id: 'BH007', market: 'UFC Heavyweight Title', selection: 'Jon Jones (Retain)', stake: '$175.00', payout: '$286.89', profit: '+$111.89', result: 'won', date: '5 days ago', sport: 'MMA' },
  { id: 'BH008', market: 'Masters Champion 2025', selection: 'Scottie Scheffler', stake: '$250.00', payout: '$0.00', profit: '-$250.00', result: 'pending', date: '6 days ago', sport: 'Golf' },
];

const ACTIVITY = [
  { icon: 'ri-checkbox-circle-line', color: 'text-teal-400', bg: 'bg-teal-500/10', label: 'Bet Won', detail: 'Chiefs ML +$450.00', time: '2 hours ago' },
  { icon: 'ri-time-line', color: 'text-amber-400', bg: 'bg-amber-500/10', label: 'Bet Placed', detail: 'Eagles -3.5 $200.00', time: '5 hours ago' },
  { icon: 'ri-add-circle-line', color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: 'Deposit', detail: '+$1,000.00', time: '1 day ago' },
  { icon: 'ri-checkbox-circle-line', color: 'text-teal-400', bg: 'bg-teal-500/10', label: 'Bet Won', detail: '3-Leg Parlay +$850.00', time: '2 days ago' },
  { icon: 'ri-close-circle-line', color: 'text-rose-400', bg: 'bg-rose-500/10', label: 'Bet Lost', detail: 'Jokic Rebounds -$150.00', time: '3 days ago' },
];

const SETTINGS_SECTIONS = [
  {
    title: 'Personal Information',
    icon: 'ri-user-settings-line',
    fields: [
      { label: 'Full Name', value: 'Alex Johnson', type: 'text' },
      { label: 'Email Address', value: 'alex.johnson@email.com', type: 'email' },
      { label: 'Phone Number', value: '+1 (555) 234-5678', type: 'tel' },
      { label: 'Date of Birth', value: '1990-06-15', type: 'date' },
    ],
  },
  {
    title: 'Notifications',
    icon: 'ri-notification-3-line',
    toggles: [
      { label: 'Email notifications for bet results', enabled: true },
      { label: 'Push notifications for price changes', enabled: false },
      { label: 'Weekly performance summary', enabled: true },
      { label: 'Promotional offers', enabled: false },
    ],
  },
];

export default function MyAccountPage() {
  const [activeTab, setActiveTab] = useState<AccountTab>('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [historyFilter, setHistoryFilter] = useState<'all' | 'won' | 'lost'>('all');
  const [toggles, setToggles] = useState([true, false, true, false]);
  const [depositOpen, setDepositOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [balance, setBalance] = useState(10000);
  const [notification, setNotification] = useState<string | null>(null);
  const [marketsDropdownOpen, setMarketsDropdownOpen] = useState(false);
  const marketsDropdownRef = useRef<HTMLDivElement>(null);

  const showNotif = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 2500);
  };

  const handleDeposit = () => {
    const amt = parseFloat(depositAmount);
    if (!isNaN(amt) && amt > 0) {
      setBalance(b => b + amt);
      setDepositOpen(false);
      setDepositAmount('');
      showNotif(`Successfully deposited $${amt.toFixed(2)}`);
    }
  };

  const handleWithdraw = () => {
    const amt = parseFloat(withdrawAmount);
    if (!isNaN(amt) && amt > 0 && amt <= balance) {
      setBalance(b => b - amt);
      setWithdrawOpen(false);
      setWithdrawAmount('');
      showNotif(`Successfully withdrew $${amt.toFixed(2)}`);
    }
  };

  const filteredHistory = historyFilter === 'all'
    ? BET_HISTORY
    : BET_HISTORY.filter(b => b.result === historyFilter);

  const TABS: { key: AccountTab; label: string; icon: string }[] = [
    { key: 'overview', label: 'Overview', icon: 'ri-dashboard-line' },
    { key: 'active-bets', label: 'Active Bets', icon: 'ri-time-line' },
    { key: 'bet-history', label: 'Bet History', icon: 'ri-history-line' },
    { key: 'settings', label: 'Settings', icon: 'ri-settings-3-line' },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#2b2b2b' }}>
      {/* Notification Toast */}
      {notification && (
        <div className="fixed top-6 right-6 z-[200] flex items-center gap-3 px-5 py-3 bg-teal-500 text-slate-900 font-semibold rounded-xl shadow-2xl animate-pulse">
          <i className="ri-check-line text-lg"></i>
          {notification}
        </div>
      )}

      {/* Demo Banner */}
      <div className="bg-amber-500 text-slate-900 px-4 py-2 z-[100] shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-center">
          <i className="ri-alert-line text-lg"></i>
          <span className="font-bold text-sm md:text-base tracking-wide uppercase">
            Demo Only — Experience the Future of Sports Betting — From Sportsbook to Prediction Market
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg border-b border-slate-700/50" style={{ background: 'rgba(43,43,43,0.97)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 gap-4">
            <a className="flex items-center gap-2 flex-shrink-0" href="/">
              <div className="h-11 w-44 flex items-center justify-center">
                <img src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png" alt="RTJ Logo" className="h-full w-full object-contain" />
              </div>
            </a>
            <div className="hidden lg:flex items-center justify-center gap-1 flex-1">
              {NAV_LINKS.filter(l => l.href !== '/markets').slice(0, 3).map(link => (
                <a key={link.href} href={link.href}>
                  <div className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${link.href === '/my-account' ? 'bg-teal-500/20 text-teal-400' : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'}`}>
                    <i className={`${link.icon} w-4 h-4 flex items-center justify-center`}></i>
                    {link.label}
                  </div>
                </a>
              ))}

              {/* Markets Dropdown */}
              <div className="relative" ref={marketsDropdownRef}>
                <button
                  onClick={() => setMarketsDropdownOpen(!marketsDropdownOpen)}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"
                >
                  <i className="ri-bar-chart-2-line w-4 h-4 flex items-center justify-center"></i>
                  Markets
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${marketsDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {marketsDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-52 rounded-xl border border-slate-700/80 shadow-2xl overflow-hidden z-50" style={{ background: 'rgba(30,30,30,0.98)' }}>
                    <div className="px-3 py-2 border-b border-slate-700/50">
                      <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Market Types</span>
                    </div>
                    {[
                      { label: 'Spreads',      icon: 'ri-bar-chart-grouped-line', color: 'text-teal-400',    path: '/markets/spreads' },
                      { label: 'Money Lines',  icon: 'ri-copper-coin-line',       color: 'text-amber-400',   path: '/markets/money-lines' },
                      { label: 'Futures',      icon: 'ri-calendar-event-line',    color: 'text-emerald-400', path: '/markets/futures' },
                      { label: 'Parlays',      icon: 'ri-stack-line',             color: 'text-violet-400',  path: '/markets/parlays' },
                      { label: 'Player Props', icon: 'ri-user-star-line',         color: 'text-cyan-400',    path: '/markets/player-props' },
                    ].map(item => (
                      <a key={item.path} href={item.path} onClick={() => setMarketsDropdownOpen(false)}>
                        <div className="flex items-center gap-3 px-4 py-2.5 text-slate-300 hover:bg-slate-800/60 hover:text-white transition-colors cursor-pointer">
                          <i className={`${item.icon} ${item.color} text-base w-4 h-4 flex items-center justify-center`}></i>
                          <span className="text-sm font-medium">{item.label}</span>
                        </div>
                      </a>
                    ))}
                    <div className="border-t border-slate-700/50 px-3 py-2">
                      <a href="/markets" onClick={() => setMarketsDropdownOpen(false)}>
                        <div className="flex items-center justify-between text-teal-400 hover:text-teal-300 transition-colors cursor-pointer text-sm font-semibold py-1">
                          <span>All Markets</span>
                          <i className="ri-arrow-right-line"></i>
                        </div>
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {NAV_LINKS.filter(l => l.href !== '/markets').slice(3).map(link => (
                <a key={link.href} href={link.href}>
                  <div className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${link.href === '/my-account' ? 'bg-teal-500/20 text-teal-400' : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'}`}>
                    <i className={`${link.icon} w-4 h-4 flex items-center justify-center`}></i>
                    {link.label}
                  </div>
                </a>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-teal-500/20 to-teal-600/10 rounded-lg border border-teal-500/30">
                <i className="ri-wallet-3-line text-teal-400 text-sm"></i>
                <span className="font-semibold text-white text-sm whitespace-nowrap">${balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              <button className="relative p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-700 transition-colors cursor-pointer">
                <i className="ri-file-list-3-line text-teal-500 text-lg w-5 h-5 flex items-center justify-center"></i>
              </button>
              <div className="p-2 rounded-lg bg-teal-500/20 border border-teal-500/40">
                <i className="ri-user-line text-teal-400 text-lg w-5 h-5 flex items-center justify-center"></i>
              </div>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors cursor-pointer">
                <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="lg:hidden border-t border-slate-700/50 py-3 space-y-1">
              {NAV_LINKS.map(link => (
                <a key={link.href} href={link.href}>
                  <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800/50 hover:text-white transition-colors cursor-pointer">
                    <i className={`${link.icon} text-base`}></i>
                    <span className="text-sm font-medium">{link.label}</span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <PageHero
        badgeLabel="My Account"
        badgeColor="teal"
        title="Account Dashboard"
        subtitle="Manage your bets, track performance, and control your account settings all in one place."
        backgroundImage="https://public.readdy.ai/ai/img_res/d1143f00e8b0b5847774371f739e8bb8.jpg"
        customContent={
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500/30 to-emerald-500/30 border-2 border-teal-500/50 flex items-center justify-center shadow-lg shadow-teal-500/20">
              <i className="ri-user-line text-teal-400 text-3xl"></i>
            </div>
            <div>
              <div className="text-slate-400 text-sm">alex.johnson@email.com</div>
            </div>
          </div>
        }
      />

      {/* Tab Bar */}
      <div className="sticky top-[57px] z-40 border-b border-slate-700/50" style={{ background: 'rgba(22,22,22,0.98)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto scrollbar-none py-2">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${
                  activeTab === tab.key
                    ? 'bg-teal-500/20 text-teal-400 border border-teal-500/40'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 border border-transparent'
                }`}
              >
                <i className={`${tab.icon}`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* ── OVERVIEW ── */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Balance Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="rounded-xl border border-slate-700/60 p-6" style={{ background: 'rgba(30,30,30,0.9)' }}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-400 text-sm font-medium">Available Balance</span>
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-500/10 rounded-lg">
                    <i className="ri-wallet-3-line text-teal-400"></i>
                  </div>
                </div>
                <div className="text-3xl font-extrabold text-white mb-1">${balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                <div className="text-teal-400 text-xs font-medium">Ready to bet</div>
              </div>
              <div className="rounded-xl border border-slate-700/60 p-6" style={{ background: 'rgba(30,30,30,0.9)' }}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-400 text-sm font-medium">Pending Bets</span>
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-500/10 rounded-lg">
                    <i className="ri-time-line text-amber-400"></i>
                  </div>
                </div>
                <div className="text-3xl font-extrabold text-white mb-1">$2,450.00</div>
                <div className="text-amber-400 text-xs font-medium">5 active bets</div>
              </div>
              <div className="rounded-xl border border-slate-700/60 p-6" style={{ background: 'rgba(30,30,30,0.9)' }}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-400 text-sm font-medium">Total Winnings</span>
                  <div className="w-8 h-8 flex items-center justify-center bg-emerald-500/10 rounded-lg">
                    <i className="ri-trophy-line text-emerald-400"></i>
                  </div>
                </div>
                <div className="text-3xl font-extrabold text-white mb-1">$8,750.00</div>
                <div className="text-emerald-400 text-xs font-medium">This month</div>
              </div>
            </div>

            {/* Quick Actions */}
            <div>
              <h2 className="text-lg font-bold text-white mb-4">Quick Actions</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { icon: 'ri-add-circle-line', label: 'Deposit Funds', color: 'from-teal-500 to-emerald-500', textColor: 'text-slate-900', action: () => setDepositOpen(true) },
                  { icon: 'ri-bank-card-line', label: 'Withdraw', color: 'from-slate-700 to-slate-600', textColor: 'text-white', action: () => setWithdrawOpen(true) },
                  { icon: 'ri-gift-line', label: 'Bonuses', color: 'from-slate-700 to-slate-600', textColor: 'text-white', action: () => showNotif('No active bonuses at this time.') },
                  { icon: 'ri-customer-service-line', label: 'Support', color: 'from-slate-700 to-slate-600', textColor: 'text-white', action: () => showNotif('Support chat coming soon!') },
                ].map((action, i) => (
                  <button
                    key={i}
                    onClick={action.action}
                    className={`flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-gradient-to-r ${action.color} ${action.textColor} font-semibold text-sm transition-all hover:opacity-90 cursor-pointer whitespace-nowrap shadow-lg`}
                  >
                    <i className={`${action.icon} text-lg`}></i>
                    {action.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Stats + Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Betting Stats */}
              <div className="rounded-xl border border-slate-700/60 p-6" style={{ background: 'rgba(30,30,30,0.9)' }}>
                <h3 className="text-white font-bold text-base mb-5 flex items-center gap-2">
                  <i className="ri-bar-chart-2-line text-teal-400"></i>
                  Betting Stats
                </h3>
                <div className="space-y-4">
                  {[
                    { label: 'Total Bets Placed', value: '247', color: 'text-white' },
                    { label: 'Win Rate', value: '58.3%', color: 'text-teal-400' },
                    { label: 'Average Bet', value: '$125.00', color: 'text-white' },
                    { label: 'Biggest Win', value: '$2,500.00', color: 'text-emerald-400' },
                    { label: 'Profit / Loss', value: '+$3,420.00', color: 'text-teal-400' },
                  ].map((stat, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-slate-700/40 last:border-0">
                      <span className="text-slate-400 text-sm">{stat.label}</span>
                      <span className={`font-bold text-sm ${stat.color}`}>{stat.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Activity */}
              <div className="rounded-xl border border-slate-700/60 p-6" style={{ background: 'rgba(30,30,30,0.9)' }}>
                <h3 className="text-white font-bold text-base mb-5 flex items-center gap-2">
                  <i className="ri-pulse-line text-teal-400"></i>
                  Recent Activity
                </h3>
                <div className="space-y-3">
                  {ACTIVITY.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-slate-700/40" style={{ background: 'rgba(255,255,255,0.02)' }}>
                      <div className={`w-9 h-9 flex items-center justify-center ${item.bg} rounded-full flex-shrink-0`}>
                        <i className={`${item.icon} ${item.color} text-base`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm font-semibold">{item.label}</div>
                        <div className="text-slate-400 text-xs truncate">{item.detail}</div>
                      </div>
                      <div className="text-slate-500 text-xs whitespace-nowrap">{item.time}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── ACTIVE BETS ── */}
        {activeTab === 'active-bets' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Active Bets <span className="text-teal-400 text-base ml-2">({ACTIVE_BETS.length})</span></h2>
              <span className="text-slate-400 text-sm">Total at risk: <span className="text-white font-semibold">$1,000.00</span></span>
            </div>
            <div className="space-y-4">
              {ACTIVE_BETS.map((bet, i) => (
                <div key={i} className="rounded-xl border border-slate-700/60 p-5 hover:border-slate-500/60 transition-all" style={{ background: 'rgba(30,30,30,0.9)' }}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${bet.status === 'live' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'}`}>
                          {bet.status === 'live' ? '● Live' : '⏳ Pending'}
                        </span>
                        <span className="text-slate-500 text-xs">{bet.sport}</span>
                        <span className="text-slate-600 text-xs">#{bet.id}</span>
                      </div>
                      <div className="text-white font-semibold text-base">{bet.market}</div>
                      <div className="text-teal-400 text-sm font-medium mt-0.5">{bet.selection}</div>
                      <div className="text-slate-500 text-xs mt-1">Implied probability: {bet.prob}</div>
                    </div>
                    <div className="flex items-center gap-6 flex-shrink-0">
                      <div className="text-center">
                        <div className="text-slate-400 text-xs mb-0.5">Stake</div>
                        <div className="text-white font-bold text-sm">{bet.stake}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-slate-400 text-xs mb-0.5">Potential Payout</div>
                        <div className="text-emerald-400 font-bold text-sm">{bet.potentialPayout}</div>
                        <div className="text-teal-500 text-xs">{bet.potentialProfit}</div>
                      </div>
                      <button
                        onClick={() => showNotif(`Sell order placed for ${bet.selection}`)}
                        className="px-4 py-2 rounded-lg border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
                      >
                        Sell Early
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── BET HISTORY ── */}
        {activeTab === 'bet-history' && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <h2 className="text-xl font-bold text-white">Bet History</h2>
              <div className="flex gap-2">
                {(['all', 'won', 'lost'] as const).map(f => (
                  <button
                    key={f}
                    onClick={() => setHistoryFilter(f)}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap capitalize ${
                      historyFilter === f
                        ? 'bg-teal-500/20 border-teal-500/50 text-teal-400'
                        : 'bg-slate-800/60 border-slate-700/50 text-slate-400 hover:border-slate-500 hover:text-slate-300'
                    }`}
                  >
                    {f === 'all' ? 'All' : f === 'won' ? '✓ Won' : '✗ Lost'}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              {filteredHistory.map((bet, i) => (
                <div key={i} className="rounded-xl border border-slate-700/60 p-5 hover:border-slate-500/60 transition-all" style={{ background: 'rgba(30,30,30,0.9)' }}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${
                          bet.result === 'won' ? 'bg-teal-500/20 text-teal-400 border-teal-500/30'
                          : bet.result === 'lost' ? 'bg-rose-500/20 text-rose-400 border-rose-500/30'
                          : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                        }`}>
                          {bet.result === 'won' ? '✓ Won' : bet.result === 'lost' ? '✗ Lost' : '⏳ Pending'}
                        </span>
                        <span className="text-slate-500 text-xs">{bet.sport}</span>
                        <span className="text-slate-600 text-xs">#{bet.id}</span>
                      </div>
                      <div className="text-white font-semibold text-sm">{bet.market}</div>
                      <div className="text-slate-400 text-xs mt-0.5 truncate">{bet.selection}</div>
                    </div>
                    <div className="flex items-center gap-6 flex-shrink-0">
                      <div className="text-center">
                        <div className="text-slate-400 text-xs mb-0.5">Stake</div>
                        <div className="text-white font-bold text-sm">{bet.stake}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-slate-400 text-xs mb-0.5">Profit / Loss</div>
                        <div className={`font-bold text-sm ${bet.profit.startsWith('+') ? 'text-teal-400' : bet.profit.startsWith('-') ? 'text-rose-400' : 'text-slate-400'}`}>
                          {bet.profit}
                        </div>
                      </div>
                      <div className="text-slate-500 text-xs whitespace-nowrap">{bet.date}</div>
                    </div>
                  </div>
                </div>
              ))}
              {filteredHistory.length === 0 && (
                <div className="text-center py-16 text-slate-500">
                  <i className="ri-history-line text-4xl mb-3 block"></i>
                  <p>No bets found for this filter.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── SETTINGS ── */}
        {activeTab === 'settings' && (
          <div className="space-y-8 max-w-2xl">
            {SETTINGS_SECTIONS.map((section, si) => (
              <div key={si} className="rounded-xl border border-slate-700/60 p-6" style={{ background: 'rgba(30,30,30,0.9)' }}>
                <h3 className="text-white font-bold text-base mb-5 flex items-center gap-2">
                  <i className={`${section.icon} text-teal-400`}></i>
                  {section.title}
                </h3>
                {section.fields && (
                  <div className="space-y-4">
                    {section.fields.map((field, fi) => (
                      <div key={fi}>
                        <label className="block text-slate-400 text-xs font-medium mb-1.5">{field.label}</label>
                        <input
                          type={field.type}
                          defaultValue={field.value}
                          className="w-full px-4 py-2.5 bg-slate-800/60 border border-slate-700/60 rounded-lg text-white text-sm focus:outline-none focus:border-teal-500 transition-colors"
                        />
                      </div>
                    ))}
                    <button
                      onClick={() => showNotif('Profile updated successfully!')}
                      className="mt-2 px-6 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-slate-900 font-bold text-sm rounded-lg cursor-pointer whitespace-nowrap hover:opacity-90 transition-opacity"
                    >
                      Save Changes
                    </button>
                  </div>
                )}
                {section.toggles && (
                  <div className="space-y-4">
                    {section.toggles.map((toggle, ti) => (
                      <div key={ti} className="flex items-center justify-between py-2 border-b border-slate-700/40 last:border-0">
                        <span className="text-slate-300 text-sm">{toggle.label}</span>
                        <button
                          onClick={() => {
                            const next = [...toggles];
                            next[ti] = !next[ti];
                            setToggles(next);
                          }}
                          className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${toggles[ti] ? 'bg-teal-500' : 'bg-slate-700'}`}
                        >
                          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${toggles[ti] ? 'translate-x-5' : 'translate-x-0'}`}></span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Danger Zone */}
            <div className="rounded-xl border border-rose-500/20 p-6" style={{ background: 'rgba(30,30,30,0.9)' }}>
              <h3 className="text-rose-400 font-bold text-base mb-4 flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                Account Actions
              </h3>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => showNotif('Account temporarily paused.')}
                  className="px-5 py-2.5 border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
                >
                  Pause Account
                </button>
                <button
                  onClick={() => showNotif('Logout successful.')}
                  className="px-5 py-2.5 border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
                >
                  Log Out
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Deposit Modal */}
      {depositOpen && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="w-full max-w-sm rounded-2xl border border-slate-700/60 p-6 shadow-2xl" style={{ background: '#1e1e1e' }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-bold text-lg">Deposit Funds</h3>
              <button onClick={() => setDepositOpen(false)} className="text-slate-400 hover:text-white cursor-pointer"><i className="ri-close-line text-xl"></i></button>
            </div>
            <label className="block text-slate-400 text-xs font-medium mb-1.5">Amount (USD)</label>
            <input
              type="number"
              min="1"
              placeholder="Enter amount..."
              value={depositAmount}
              onChange={e => setDepositAmount(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800/60 border border-slate-700/60 rounded-lg text-white text-sm focus:outline-none focus:border-teal-500 transition-colors mb-4"
            />
            <div className="flex gap-2 flex-wrap mb-4">
              {['100', '250', '500', '1000'].map(amt => (
                <button key={amt} onClick={() => setDepositAmount(amt)} className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 text-xs hover:border-teal-500 hover:text-teal-400 transition-all cursor-pointer whitespace-nowrap">${amt}</button>
              ))}
            </div>
            <button onClick={handleDeposit} className="w-full py-3 bg-gradient-to-r from-teal-500 to-emerald-500 text-slate-900 font-bold rounded-lg cursor-pointer whitespace-nowrap hover:opacity-90 transition-opacity">
              Confirm Deposit
            </button>
          </div>
        </div>
      )}

      {/* Withdraw Modal */}
      {withdrawOpen && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="w-full max-w-sm rounded-2xl border border-slate-700/60 p-6 shadow-2xl" style={{ background: '#1e1e1e' }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-bold text-lg">Withdraw Funds</h3>
              <button onClick={() => setWithdrawOpen(false)} className="text-slate-400 hover:text-white cursor-pointer"><i className="ri-close-line text-xl"></i></button>
            </div>
            <div className="mb-4 p-3 bg-slate-800/60 rounded-lg border border-slate-700/40">
              <span className="text-slate-400 text-xs">Available Balance: </span>
              <span className="text-teal-400 font-bold text-sm">${balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
            </div>
            <label className="block text-slate-400 text-xs font-medium mb-1.5">Amount (USD)</label>
            <input
              type="number"
              min="1"
              max={balance}
              placeholder="Enter amount..."
              value={withdrawAmount}
              onChange={e => setWithdrawAmount(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800/60 border border-slate-700/60 rounded-lg text-white text-sm focus:outline-none focus:border-teal-500 transition-colors mb-4"
            />
            <button onClick={handleWithdraw} className="w-full py-3 bg-gradient-to-r from-slate-600 to-slate-500 text-white font-bold rounded-lg cursor-pointer whitespace-nowrap hover:opacity-90 transition-opacity">
              Confirm Withdrawal
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-slate-700/50 py-8 px-4 mt-8" style={{ background: '#1e1e1e' }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="h-10 w-40 flex items-center justify-center">
            <img
              src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png"
              alt="RTJ Logo"
              className="h-full w-full object-contain"
            />
          </div>
          <p className="text-slate-600 text-xs text-center">Demo only. Not real money. For educational purposes only. Must be 18+ to participate.</p>
          <div className="flex items-center gap-4 text-slate-500 text-xs">
            <a href="/" className="hover:text-slate-300 transition-colors cursor-pointer">Home</a>
            <a href="/markets" className="hover:text-slate-300 transition-colors cursor-pointer">Markets</a>
            <a href="/why-rtj" className="hover:text-slate-300 transition-colors cursor-pointer">Why L2L</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
